import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

export const CartContext = createContext(null);

const normalizeCart = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item) => item && Number.isInteger(Number(item.id)))
    .map((item) => ({
      id: Number(item.id),
      size: String(item.size || ""),
      color: String(item.color || ""),
      quantity: Math.max(1, Math.min(10, Number(item.quantity) || 1)),
    }))
    .filter((item) => item.size && item.color);
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      return normalizeCart(JSON.parse(window.localStorage.getItem("nartvmora-cart") || "[]"));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    window.localStorage.setItem("nartvmora-cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (productId, quantity = 1, size, color) => {
    const safeQuantity = Math.max(1, Math.min(10, Number(quantity) || 1));
    const id = Number(productId);
    if (!Number.isInteger(id) || !size || !color) return;

    setCartItems((items) => {
      const match = items.find((item) => item.id === id && item.size === size && item.color === color);
      if (!match) return [...items, { id, size, color, quantity: safeQuantity }];

      return items.map((item) =>
        item === match ? { ...item, quantity: Math.min(10, item.quantity + safeQuantity) } : item
      );
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCartItems((items) =>
      items.filter((item) => !(item.id === Number(productId) && item.size === size && item.color === color))
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const safeQuantity = Math.max(1, Math.min(10, Number(quantity) || 1));
    setCartItems((items) =>
      items.map((item) =>
        item.id === Number(productId) && item.size === size && item.color === color
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  };

  const clearCart = useCallback(() => setCartItems([]), []);
  const itemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  const value = useMemo(
    () => ({ cartItems, itemCount, addToCart, removeFromCart, updateQuantity, clearCart }),
    [cartItems, itemCount, clearCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
